import { Plus, Copy, Info, Trash2, Upload, X } from "lucide-react";
import "./DesignRequestForm.css";
import { artworkPoRequired } from "../artworkPoRulesV52A1";

export const PRIORITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

export const CAN_SIZE_OPTIONS = [
  "5.5oz",
  "6.8oz",
  "7.5oz FIT",
  "8oz Sleek",
  "8.4oz Slim",
  "10oz Sleek",
  "12oz Standard",
  "12oz Sleek",
  "16oz Standard",
  "19.2oz",
  "24oz",
  "25.4oz",
  "32oz",
  "Other",
];

export const CAN_FINISH_OPTIONS = ["200", "202", "206", "209"];
export const LID_TYPE_OPTIONS = [
  "SOT (Stay-On-Tab)",
  "LOE (Large Opening End)",
  "Full Aperture",
  "CDL",
  "Standard",
  "Other",
];
export const PASTEURIZATION_OPTIONS = [
  "Not Required",
  "Up to 140°F",
  "141–160°F",
  "161–180°F",
  "181°F and above",
];
export const SPECIALTY_INK_OPTIONS = [
  "None",
  "Metallic",
  "Fluorescent",
  "Thermochromic",
  "Other",
];
export const OVERVARNISH_OPTIONS = ["Gloss", "Matte", "Satin", "Tactile", "Retort"];

const CONVERSION_HELP = "A HARD conversion is a revised graphic with a significant change that CANNOT be mixed with old inventories. If you select HARD, you are directing Ball to stop producing this graphic by the selected date and to invoice any existing inventories.";
const ABV_HELP = "Alcohol by Volume, which is the standard measure of the percentage of pure alcohol (ethanol) contained in a given volume of an alcoholic beverage.";
const PH_HELP = "If you don't know the pH, you can leave it blank.";

const REQUIRED_ISR_NAMES = ["Jen Horstmann", "Leanne Darrington"];
const REQUIRED_OSR_NAMES = ["Nick Wilkins", "Servando Plascencia", "Tony Galletti"];

const mergeSortedNames = (names, requiredNames) => {
  const unique = new Map();
  [...(names || []), ...requiredNames].forEach((name) => {
    const cleaned = String(name || "").trim();
    if (cleaned) unique.set(cleaned.toLowerCase(), cleaned);
  });
  return [...unique.values()].sort((left, right) => left.localeCompare(right));
};

const manufacturerLabel = (name) => {
  const normalized = String(name || "").toLowerCase();
  if (normalized.includes("canpack")) return "Canpack";
  if (normalized.includes("ball")) return "Ball Corp";
  return name;
};

export const blankSku = () => ({
  brand: "",
  sku_name: "",
  sku_number: "",
  upc_code: "",
  number_of_colors: "",
  pasteurization_range: "Not Required",
  specialty_ink_type: "None",
  specialty_ink_other: "",
  overvarnish_type: "",
  pilot_requested: false,
  pilot_ship_to_address: "",
  reference_collateral_sent: false,
  requesting_drawdowns: false,
  liquid_testing: false,
  abv_percent: "",
  ph: "",
  sample_refrigeration_required: "",
  syrup_sample: "",
  throw_ratio: "",
  sku_notes: "",
});

export const emptyDesignRequestForm = {
  customer_id: "",
  company_name: "",
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  manufacturer_id: "",
  manufacturer_name: "",
  priority: "medium",
  deadline: "",
  gamer_po: "",
  customer_po: "",
  isr: "",
  osr: "",
  copacker: "",
  can_size: "",
  can_size_other: "",
  design_type: "",
  conversion_type: "",
  conversion_date: "",
  previous_vendor_item_number: "",
  customer_part_number: "",
  beverage_type: "",
  application_type: "",
  can_finish: "",
  lid_type: "",
  lid_type_other: "",
  lid_color: "",
  tab_color: "",
  comments: "",
};

export const DesignRequestFormFields = ({
  form,
  setForm,
  skuItems,
  setSkuItems,
  options = { customers: [], manufacturers: [], isr_list: [], osr_list: [] },
  attachments = [],
  onFilesSelected,
  onRemoveAttachment,
  uploadingFiles = false,
}) => {
  const update = (name, value) => setForm((previous) => ({ ...previous, [name]: value }));

  const ballCanpack = (options.manufacturers || [])
    .filter((manufacturer) => {
      const name = String(manufacturer.name || "").toLowerCase();
      return name.includes("ball") || name.includes("canpack");
    })
    .map((manufacturer) => ({ ...manufacturer, display_name: manufacturerLabel(manufacturer.name) }))
    .sort((left, right) => left.display_name.localeCompare(right.display_name));

  const isrNames = mergeSortedNames(options.isr_list, REQUIRED_ISR_NAMES);
  const osrNames = mergeSortedNames(options.osr_list, REQUIRED_OSR_NAMES);

  const handleManufacturerChange = (value) => {
    const manufacturer = ballCanpack.find((item) => item.id === value);
    setForm((previous) => ({
      ...previous,
      manufacturer_id: value,
      manufacturer_name: manufacturer?.name || "",
    }));
  };

  const handleDesignTypeChange = (value) => {
    setForm((previous) => ({
      ...previous,
      design_type: value,
      ...(value === "Revision" ? {} : {
        conversion_type: "",
        conversion_date: "",
        previous_vendor_item_number: "",
      }),
    }));
  };

  const updateSku = (index, field, value) => {
    setSkuItems((previous) => previous.map((sku, skuIndex) => (
      skuIndex === index ? { ...sku, [field]: value } : sku
    )));
  };

  const handleLiquidTestingChange = (index, checked) => {
    setSkuItems((previous) => previous.map((sku, skuIndex) => {
      if (skuIndex !== index) return sku;
      return {
        ...sku,
        liquid_testing: checked,
        ...(checked ? {} : {
          abv_percent: "",
          ph: "",
          sample_refrigeration_required: "",
          syrup_sample: "",
          throw_ratio: "",
        }),
      };
    }));
  };

  const handleSyrupSampleChange = (index, value) => {
    setSkuItems((previous) => previous.map((sku, skuIndex) => (
      skuIndex === index
        ? { ...sku, syrup_sample: value, throw_ratio: value === "Yes" ? sku.throw_ratio : "" }
        : sku
    )));
  };

  const addSku = () => setSkuItems((previous) => [...previous, blankSku()]);
  const duplicateSku = (index) => {
    setSkuItems((previous) => {
      const next = [...previous];
      next.splice(index + 1, 0, { ...previous[index] });
      return next;
    });
  };
  const removeSku = (index) => {
    setSkuItems((previous) => previous.length === 1 ? previous : previous.filter((_, skuIndex) => skuIndex !== index));
  };

  return (
    <>
      <div className="dof-section-title">CUSTOMER CONTACT</div>
      <div className="dof-two-col">
        <div className="dof-col">
          <InputField label="Customer" value={form.company_name} onChange={(value) => update("company_name", value)} required />
          <InputField label="Contact Name" value={form.contact_name} onChange={(value) => update("contact_name", value)} required />
        </div>
        <div className="dof-col">
          <InputField label="Contact Email" type="email" value={form.contact_email} onChange={(value) => update("contact_email", value)} required />
          <InputField label="Contact Phone" value={form.contact_phone} onChange={(value) => update("contact_phone", value)} />
        </div>
      </div>

      <div className="dof-section-title">PROJECT ASSIGNMENT</div>
      <div className="dof-two-col">
        <div className="dof-col">
          <SelectField
            label="Manufacturer"
            value={form.manufacturer_id}
            onChange={handleManufacturerChange}
            required
            options={ballCanpack.map((item) => ({ value: item.id, label: item.display_name }))}
          />
          <SelectField label="Priority" value={form.priority} onChange={(value) => update("priority", value)} options={PRIORITY_OPTIONS} />
          <SelectField
            label="Inside Sales Rep (ISR)"
            value={form.isr}
            onChange={(value) => update("isr", value)}
            required
            options={isrNames.map((name) => ({ value: name, label: name }))}
          />
          <SelectField
            label="Outside Sales Rep (OSR)"
            value={form.osr}
            onChange={(value) => update("osr", value)}
            required
            options={osrNames.map((name) => ({ value: name, label: name }))}
          />
        </div>
        <div className="dof-col">
          <InputField label="Artwork PO" value={form.gamer_po} onChange={(value) => update("gamer_po", value)} required={artworkPoRequired(form.manufacturer_name || form.manufacturer || "")} />
          <InputField label="Customer PO" value={form.customer_po} onChange={(value) => update("customer_po", value)} />
          <InputField label="Need By Date" type="date" value={form.deadline} onChange={(value) => update("deadline", value)} />
        </div>
      </div>

      <div className="dof-section-title">GENERAL INFO</div>
      <div className="dof-two-col">
        <div className="dof-col">
          <SelectField label="Design Type" value={form.design_type} onChange={handleDesignTypeChange} required options={["New Design", "Revision"].map((value) => ({ value, label: value }))} />
          {form.design_type === "Revision" && (
            <>
              <SelectField label="Conversion Type" helpText={CONVERSION_HELP} value={form.conversion_type} onChange={(value) => update("conversion_type", value)} required options={["Hard Conversion", "Soft Conversion"].map((value) => ({ value, label: value }))} />
              <InputField label="Conversion Date" type="date" value={form.conversion_date} onChange={(value) => update("conversion_date", value)} required />
              <InputField label="Previous Vendor Item Number" value={form.previous_vendor_item_number} onChange={(value) => update("previous_vendor_item_number", value)} />
            </>
          )}
          <InputField label="Customer Part Number" value={form.customer_part_number} onChange={(value) => update("customer_part_number", value)} />
          <SelectField label="Application Type" value={form.application_type} onChange={(value) => update("application_type", value)} options={["Offset Printing", "Digital Printing", "Shrink Sleeve", "Pressure Sensitive Label", "Direct Print"].map((value) => ({ value, label: value }))} />
          <SelectField label="Beverage Type" value={form.beverage_type} onChange={(value) => update("beverage_type", value)} options={["Beer", "Hard Seltzer", "RTD Cocktail", "Energy Drink", "Sparkling Water", "Soda", "Juice", "Wine", "Cider", "Non-Alcoholic", "Other"].map((value) => ({ value, label: value }))} />
        </div>
        <div className="dof-col">
          <SelectField label="Can Size" value={form.can_size} onChange={(value) => update("can_size", value)} required options={CAN_SIZE_OPTIONS.map((value) => ({ value, label: value }))} />
          {form.can_size === "Other" && <InputField label="Other Can Size" value={form.can_size_other} onChange={(value) => update("can_size_other", value)} required />}
          <SelectField label="Can Finish" value={form.can_finish} onChange={(value) => update("can_finish", value)} required options={CAN_FINISH_OPTIONS.map((value) => ({ value, label: value }))} />
          <SelectField label="Lid Type" value={form.lid_type} onChange={(value) => update("lid_type", value)} options={LID_TYPE_OPTIONS.map((value) => ({ value, label: value }))} />
          {form.lid_type === "Other" && <InputField label="Other Lid Type" value={form.lid_type_other} onChange={(value) => update("lid_type_other", value)} />}
          <InputField label="Lid Color" value={form.lid_color} onChange={(value) => update("lid_color", value)} />
          <InputField label="Tab Color" value={form.tab_color} onChange={(value) => update("tab_color", value)} />
        </div>
      </div>

      <div className="dof-section-title">SKU DETAILS</div>
      <div className="dof-note" style={{ marginBottom: "1rem" }}>
        <div className="dof-note-title">MULTIPLE SKUS</div>
        <div className="dof-note-text">Duplicate a SKU to carry the shared information forward, then change only what differs.</div>
      </div>

      {skuItems.map((sku, index) => (
        <div key={index} className="dof-contact-sub" style={{ marginBottom: "1rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
            <h4 className="dof-contact-heading">SKU {index + 1}</h4>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button type="button" onClick={() => duplicateSku(index)} className="dof-back-btn"><Copy size={14} /> Duplicate SKU</button>
              <button type="button" onClick={() => removeSku(index)} disabled={skuItems.length === 1} className="dof-back-btn" style={{ opacity: skuItems.length === 1 ? 0.5 : 1 }}><Trash2 size={14} /> Remove</button>
            </div>
          </div>

          <div className="dof-prod-grid">
            <InputField label="Brand" value={sku.brand} onChange={(value) => updateSku(index, "brand", value)} />
            <InputField label="SKU Name" value={sku.sku_name} onChange={(value) => updateSku(index, "sku_name", value)} />
            <InputField label="SKU Number" value={sku.sku_number} onChange={(value) => updateSku(index, "sku_number", value)} />
            <InputField label="UPC" value={sku.upc_code} onChange={(value) => updateSku(index, "upc_code", value)} />
            <InputField label="Number of Colors" value={sku.number_of_colors} onChange={(value) => updateSku(index, "number_of_colors", value)} />
            <SelectField label="Specialty Inks" value={sku.specialty_ink_type} onChange={(value) => updateSku(index, "specialty_ink_type", value)} options={SPECIALTY_INK_OPTIONS.map((value) => ({ value, label: value }))} />
            {sku.specialty_ink_type === "Other" && <InputField label="Other Specialty Ink" value={sku.specialty_ink_other} onChange={(value) => updateSku(index, "specialty_ink_other", value)} />}
            <SelectField label="Overvarnish" value={sku.overvarnish_type} onChange={(value) => updateSku(index, "overvarnish_type", value)} options={OVERVARNISH_OPTIONS.map((value) => ({ value, label: value }))} />
          </div>

          <div className="dof-mfg-note" style={{ padding: "10px 12px", margin: "8px 0 14px" }}>
            Overvarnish and inside varnish content are standardized as <strong>BPANI-PFASNI</strong>.
          </div>

          <div className="dof-checks-grid">
            <CheckboxField label="Pilot Requested" checked={!!sku.pilot_requested} onChange={(checked) => updateSku(index, "pilot_requested", checked)} />
            <CheckboxField label="Reference / Collateral Sent" checked={!!sku.reference_collateral_sent} onChange={(checked) => updateSku(index, "reference_collateral_sent", checked)} />
            <CheckboxField label="Requesting Drawdowns" checked={!!sku.requesting_drawdowns} onChange={(checked) => updateSku(index, "requesting_drawdowns", checked)} />
            <CheckboxField label="Liquid Testing" checked={!!sku.liquid_testing} onChange={(checked) => handleLiquidTestingChange(index, checked)} />
          </div>

          <div className="dof-prod-grid dof-sku-support-grid">
            <SelectField label="Pasteurization" value={sku.pasteurization_range} onChange={(value) => updateSku(index, "pasteurization_range", value)} options={PASTEURIZATION_OPTIONS.map((value) => ({ value, label: value }))} />
          </div>

          {sku.pilot_requested && (
            <TextAreaField label="Pilot Ship-To Address" value={sku.pilot_ship_to_address} onChange={(value) => updateSku(index, "pilot_ship_to_address", value)} required />
          )}

          {sku.liquid_testing && (
            <div className="dof-conditional-panel">
              <div className="dof-conditional-title">LIQUID TESTING DETAILS</div>
              <div className="dof-prod-grid">
                <InputField label="ABV %" helpText={ABV_HELP} inputMode="decimal" value={sku.abv_percent} onChange={(value) => updateSku(index, "abv_percent", value)} />
                <InputField label="pH" helpText={PH_HELP} inputMode="decimal" value={sku.ph} onChange={(value) => updateSku(index, "ph", value)} />
                <SelectField label="Sample Refrigeration Required" value={sku.sample_refrigeration_required} onChange={(value) => updateSku(index, "sample_refrigeration_required", value)} options={["Yes", "No"].map((value) => ({ value, label: value }))} />
                <SelectField label="Syrup Sample" value={sku.syrup_sample} onChange={(value) => handleSyrupSampleChange(index, value)} options={["Yes", "No"].map((value) => ({ value, label: value }))} />
                {sku.syrup_sample === "Yes" && <InputField label="Throw Ratio" value={sku.throw_ratio} onChange={(value) => updateSku(index, "throw_ratio", value)} />}
              </div>
            </div>
          )}

          <TextAreaField label="SKU Notes" value={sku.sku_notes} onChange={(value) => updateSku(index, "sku_notes", value)} />
        </div>
      ))}

      <div style={{ textAlign: "center", margin: "1rem 0 2rem" }}>
        <button type="button" onClick={addSku} className="dof-back-btn"><Plus size={14} /> Add SKU</button>
      </div>

      <div className="dof-section-title">ARTWORK FILES</div>
      <div className="dof-contact-sub artwork-upload-panel">
        <label className="artwork-upload-button">
          <Upload size={18} />
          {uploadingFiles ? "Uploading..." : "Upload Artwork"}
          <input
            type="file"
            multiple
            disabled={uploadingFiles}
            accept=".pdf,.ai,.eps,.psd,.svg,.png,.jpg,.jpeg,.tif,.tiff,.zip"
            onChange={(event) => {
              if (onFilesSelected) onFilesSelected(Array.from(event.target.files || []));
              event.target.value = "";
            }}
          />
        </label>
        <p className="dof-note-text">Accepted: PDF, AI, EPS, PSD, SVG, PNG, JPG, TIFF, and ZIP. Maximum 50 MB per file.</p>
        {attachments.length > 0 && (
          <div className="artwork-upload-list">
            {attachments.map((attachment, index) => (
              <div key={attachment.id || `${attachment.filename}-${index}`} className="artwork-upload-item">
                <span>{attachment.filename}</span>
                <small>{attachment.size_bytes ? `${(attachment.size_bytes / 1024 / 1024).toFixed(2)} MB` : ""}</small>
                {onRemoveAttachment && (
                  <button type="button" onClick={() => onRemoveAttachment(index)} aria-label={`Remove ${attachment.filename}`}><X size={15} /></button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="dof-section-title">COMMENTS / REMARKS</div>
      <TextAreaField label="" value={form.comments} onChange={(value) => update("comments", value)} />
    </>
  );
};

const InfoTooltip = ({ text }) => (
  <span className="form-info-tooltip">
    <button type="button" className="form-info-button" aria-label="More information" title={text}>
      <Info size={15} strokeWidth={2.2} />
    </button>
    <span className="form-info-content" role="tooltip">{text}</span>
  </span>
);

const FieldLabel = ({ label, required, helpText }) => (
  <label className="form-label form-label-with-help">
    <span>{label}{required && <span style={{ color: "#ef4444" }}> *</span>}</span>
    {helpText && <InfoTooltip text={helpText} />}
  </label>
);

export const InputField = ({ label, value, onChange, type = "text", required = false, helpText, inputMode }) => (
  <div className="form-field">
    <FieldLabel label={label} required={required} helpText={helpText} />
    <input type={type} inputMode={inputMode} value={value || ""} onChange={(event) => onChange(event.target.value)} className="form-input" required={required} />
  </div>
);

export const SelectField = ({ label, value, onChange, options = [], required = false, helpText }) => (
  <div className="form-field">
    <FieldLabel label={label} required={required} helpText={helpText} />
    <select value={value || ""} onChange={(event) => onChange(event.target.value)} className="form-input" required={required}>
      <option value="">Select...</option>
      {options.map((option) => {
        const normalized = typeof option === "string" ? { value: option, label: option } : option;
        return <option key={normalized.value} value={normalized.value}>{normalized.label}</option>;
      })}
    </select>
  </div>
);

export const TextAreaField = ({ label, value, onChange, required = false, helpText }) => (
  <div className="form-field">
    {label && <FieldLabel label={label} required={required} helpText={helpText} />}
    <textarea value={value || ""} onChange={(event) => onChange(event.target.value)} className="form-input" rows={3} required={required} />
  </div>
);

export const CheckboxField = ({ label, checked, onChange }) => (
  <label className="dof-check-item">
    <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
    <span>{label}</span>
  </label>
);
